/* eslint-disable max-lines */
import {
    useState, useEffect, useMemo
} from 'react';
import {
    cn
} from 'shared/lib/utils';
import {
    Play, Maximize2, Minimize2, Code
} from 'lucide-react';
import ApiTestPanel from 'builders/destudio/components/ApiTestPanel';
import EntityJsonView from 'builders/destudio/components/EntityJsonView';
import {
    Button
} from 'shared/components/ui/button';
import {
    Input
} from 'shared/components/ui/input';
import {
    Label
} from 'shared/components/ui/label';
import {
    Textarea
} from 'shared/components/ui/textarea';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    Plus, Trash2
} from 'lucide-react';
import {
    Tabs, TabsContent, TabsList, TabsTrigger
} from 'shared/components/ui/tabs';
import {
    Alert, AlertDescription
} from 'shared/components/ui/alert';
import {
    AlertCircle
} from 'lucide-react';
import {
    SchemaSelector
} from './SchemaSelector';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle
} from 'shared/components/ui/dialog';
import {
    JSONSchemaBuilderDialog
} from './Components/JSONSchemaBuilderDialog';
import {
    Wrench
} from 'lucide-react';
import {
    isSensitiveHeader, isSensitiveHeaderName,
} from 'shared/api/services/BEX/apiManager.service';

import {
    normalizeParamsDefForEditor, serializeParamsDef, paramKeyList
} from './paramsDef';

function hydrateApiFormState(api) {
    const base = { ...(api || {}) };
    const headers = { ...(base.headers || {}) };
    const configured = Array.isArray(base.secret_headers_configured)
        ? base.secret_headers_configured
        : [];
    // Re-surface vault-backed header names as empty write-only rows.
    configured.forEach((name) => {
        if (!(name in headers)) headers[name] = '';
    });
    // Also treat any leftover sensitive plaintext as configured (legacy).
    Object.entries(headers).forEach(([name, value]) => {
        if (isSensitiveHeader(name, String(value ?? ''))) {
            headers[name] = '';
            if (!configured.includes(name)) configured.push(name);
        }
    });
    return {
        ...base,
        headers,
        paramsDef: normalizeParamsDefForEditor(base.paramsDef),
        secret_headers_configured: configured,
    };
}

export function ApiForm({
    api, onSave, onCancel, open, setOpen
}) {
    const [formData, setFormData] = useState(() => hydrateApiFormState(api));
    // ─── DE Studio side-by-side test pane (additive — pre-destudio
    // callers passed `api={undefined}` so isEdit stays false and the
    // toggle never shows). Mirrors InstructionFormDialog + OpcodeBuilder.
    const [showTest, setShowTest] = useState(true);
    const [testMaximized, setTestMaximized] = useState(false);
    const [showJson, setShowJson] = useState(false);
    const [sendSignal, setSendSignal] = useState(0);
    // Compare against hydrated shape (secret header rows, etc.) — raw `api`
    // from Redis never matches after hydrateApiFormState, which falsely marked
    // every saved API as dirty and disabled Send.
    const initialSnapshot = useMemo(
        () => JSON.stringify(hydrateApiFormState(api)),
        [api],
    );
    const currentSnapshot = JSON.stringify(formData || {});
    const pristine = currentSnapshot === initialSnapshot;
    const [activeTab, setActiveTab] = useState('params');
    const [jsonError, setJsonError] = useState('');
    const [btnLoading, setBtnLoading] = useState(false);
    const [schemaError, setSchemaError] = useState('');
    const [paramKeyError, setParamKeyError] = useState('');
    const [headerKeyError, setHeaderKeyError] = useState('');
    const [headerDisplayValues, setHeaderDisplayValues] = useState({});
    const [authError, setAuthError] = useState('');
    // Write-only secret inputs (Vault) — never hydrated from existing docs.
    // On edit, leave empty to keep the existing *_ref / legacy secret.
    const [newBearerToken, setNewBearerToken] = useState('');
    const [newBasicPassword, setNewBasicPassword] = useState('');
    // Sensitive header values typed in this session (name -> new value).
    const [newHeaderSecrets, setNewHeaderSecrets] = useState({});
    const [inputSchemaBuilderOpen, setInputSchemaBuilderOpen] = useState(false);
    const [outputSchemaBuilderOpen, setOutputSchemaBuilderOpen] = useState(false);

    // Auth-type-aware: don't treat a bearer token_ref as "password configured"
    // when the user switches to basic (or vice versa).
    const hasConfiguredSecret = (() => {
        const t = formData.auth?.type || 'none';
        if (t === 'bearer') {
            return Boolean(formData.auth?.token_ref || formData.auth?.secrets_configured);
        }
        if (t === 'basic') {
            return Boolean(formData.auth?.password_ref);
        }
        if (t === 'apikey') {
            return Boolean(formData.auth?.value_ref || formData.auth?.secrets_configured);
        }
        return false;
    })();
    const isEdit = Boolean(formData._id);

    // Postman-style path variables: keep the `in:'path'` params in SYNC with the
    // {{var}} / :var tokens in the URL — add new ones, drop ones no longer present
    // (so editing a variable name doesn't pile up stale params). Values are
    // preserved for tokens that stay; query/header/body params are left untouched.
    useEffect(() => {
        const url = formData?.url || '';
        const tokens = [];
        const seen = new Set();
        const push = (t) => { if (!seen.has(t)) { seen.add(t); tokens.push(t); } };
        (url.match(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g) || [])
            .forEach((m) => push(m.replace(/[{}\s]/g, '')));
        (url.match(/:([a-zA-Z_][a-zA-Z0-9_]*)/g) || [])
            .forEach((m) => push(m.slice(1)));
        setFormData((prev) => {
            const params = prev.paramsDef || [];
            const prevPath = params.filter((p) => p.in === 'path');
            const prevPathKeys = prevPath.map((p) => p.key);
            // No change to the path-var set/order → bail (avoids render loops).
            if (prevPathKeys.length === tokens.length
                && prevPathKeys.every((k, i) => k === tokens[i])) {
                return prev;
            }
            const byKey = new Map(prevPath.map((p) => [p.key, p]));
            const nonPath = params.filter((p) => p.in !== 'path');
            const nextPath = tokens.map((t) => byKey.get(t) || { key: t, value: '', in: 'path' });
            return { ...prev, paramsDef: [...nonPath, ...nextPath] };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData?.url]);

    const validateParamKeys = (params) => {
        // Defensive on purpose: hydrate normalises, but this also runs on rows
        // straight out of state (add/update/remove/submit) and a keyless row
        // used to take the whole page down.
        const keys = paramKeyList(params);
        const duplicates = keys.filter((key, index) => keys.indexOf(key) !== index);

        if (duplicates.length > 0) {
            const uniqueDuplicates = Array.from(new Set(duplicates));
            setParamKeyError(`Duplicate key name(s) found: ${uniqueDuplicates.join(', ')}`);
            return false;
        }
        setParamKeyError('');
        return true;
    };

    const validateHeaderKeys = (headers, excludeKey = null) => {
        // Use display values if available (what user sees/types), otherwise use actual keys
        const keys = Object.keys(headers).map(key => {
            const displayValue = headerDisplayValues[key];
            return displayValue !== undefined ? displayValue.trim() : key.trim();
        }).filter(key => key !== '');
        // Filter out the key being edited if provided
        const keysToCheck = excludeKey ? keys.filter(key => key !== excludeKey) : keys;
        const duplicates = keysToCheck.filter((key, index) => keysToCheck.indexOf(key) !== index);

        if (duplicates.length > 0) {
            const uniqueDuplicates = Array.from(new Set(duplicates));
            setHeaderKeyError(`Duplicate key name(s) found: ${uniqueDuplicates.join(', ')}`);
            return false;
        }
        setHeaderKeyError('');
        return true;
    };

    // Re-hydrate when the dialog opens / target API changes.
    useEffect(() => {
        if (!open) return;
        const hydrated = hydrateApiFormState(api);
        setFormData(hydrated);
        setNewBearerToken('');
        setNewBasicPassword('');
        setNewHeaderSecrets({});
        if (hydrated.paramsDef && hydrated.paramsDef.length > 0) {
            validateParamKeys(hydrated.paramsDef);
        }
        if (hydrated.headers && Object.keys(hydrated.headers).length > 0) {
            validateHeaderKeys(hydrated.headers);
            const displayValues = {};
            Object.keys(hydrated.headers).forEach((key) => {
                displayValues[key] = key;
            });
            setHeaderDisplayValues(displayValues);
        } else {
            setHeaderDisplayValues({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, api]);

    const handleChange = (e) => {
        const {
            name, value
        } = e.target;
        if (name === 'timeout') {
            const parsedValue = Number(value);
            setFormData({
                ...formData,
                [name]: parsedValue < 500 ? 500 : parsedValue
            });
        } else {
            setFormData({
                ...formData,
                [name]: value
            });
        }

        // Validate JSON if body or schema is being changed
        if (name === 'input_schema' && value.trim()) {
            try {
                JSON.parse(value);
                setJsonError('');
            } catch (error) {
                setJsonError(error.message);
            }
        } else if (name === 'input_schema' && !value.trim()) {
            setJsonError('');
        }

        // Validate response schema JSON
        if (name === 'output_schema' && value.trim()) {
            try {
                JSON.parse(value);
                setSchemaError('');
            } catch (error) {
                setSchemaError(error.message);
            }
        } else if (name === 'output_schema' && !value.trim()) {
            setSchemaError('');
        }
    };

    const handleMethodChange = (value) => {
        setFormData({
            ...formData,
            method: value
        });
    };

    // Postman-style typing: let users write `:order_id` and store it as the
    // `{{order_id}}` Jinja2 form the engine substitutes (ApiManager._render_template).
    // `:var` must start with a letter/underscore so `https://` and `:8080` are left alone.
    const colonToBrace = (url) => (url || '').replace(/:([a-zA-Z_][a-zA-Z0-9_]*)/g, '{{$1}}');

    const handleAuthTypeChange = (value) => {
        if (
            formData.auth?.type
            && formData.auth.type !== 'none'
            && value !== formData.auth.type
            && hasConfiguredSecret
        ) {
            // eslint-disable-next-line no-alert
            if (!window.confirm('Changing auth type will invalidate the current secret — continue?')) {
                return;
            }
        }
        setFormData({
            ...formData,
            auth: {
                type: value,
                ...(value === 'basic' ? { username: formData.auth?.username || '' } : {}),
            },
        });
        setNewBearerToken('');
        setNewBasicPassword('');
        setAuthError('');
    };

    const handleAuthValueChange = (e) => {
        const {
            name, value
        } = e.target;
        // username stays in L1; secrets use write-only state above
        if (name === 'username') {
            setFormData({
                ...formData,
                auth: {
                    ...formData.auth,
                    username: value
                },
            });
        }
        setAuthError('');
    };

    const addHeader = () => {
        const emptyKey = '';
        setFormData({
            ...formData,
            headers: {
                ...formData.headers,
                [emptyKey]: ''
            },
        });
        setHeaderDisplayValues({
            ...headerDisplayValues,
            [emptyKey]: ''
        });
    };

    const addParams = () => {
        setFormData({
            ...formData,
            paramsDef: [...formData.paramsDef || [], {
                key: '',
                value: ''
            }],
        });
    };

    const updateQueryParams = (type, index, field, value) => {
        const updatedHeaders = [...formData[type]];
        updatedHeaders[index] = {
            ...updatedHeaders[index],
            [field]: value
        };
        const updatedFormData = {
            ...formData,
            [type]: updatedHeaders
        };
        setFormData(updatedFormData);

        // Validate param keys whenever they change
        if (type === 'paramsDef' && field === 'key') {
            validateParamKeys(updatedHeaders);
        }
    };

    const updateHeader = (type, key, newValue) => {
        const updated = {
            ...formData[type],
            [key]: newValue
        };
        setFormData({
            ...formData,
            [type]: updated
        });
    };

    const updateHeaderKey = (type, oldKey, newKey) => {
        const current = formData[type];
        const trimmedNewKey = newKey.trim();

        // Update display value immediately so user sees what they're typing
        setHeaderDisplayValues({
            ...headerDisplayValues,
            [oldKey]: newKey
        });

        // If the new key is empty, allow it (user might be deleting to retype)
        if (!trimmedNewKey) {
            const {
                [oldKey]: value, ...rest
            } = current;
            const updated = {
                ...rest,
                '': value
            };
            setFormData({
                ...formData,
                [type]: updated
            });
            setHeaderDisplayValues({
                ...headerDisplayValues,
                '': newKey,
                [oldKey]: undefined
            });
            setHeaderKeyError('');
            validateHeaderKeys(updated);
            return;
        }

        // Check if the new key (excluding the old key) already exists in display values or actual keys
        const otherKeys = Object.keys(current)
            .filter(key => key !== oldKey)
            .map(key => {
                const displayValue = headerDisplayValues[key];
                return displayValue !== undefined ? displayValue.trim() : key.trim();
            });
        if (otherKeys.includes(trimmedNewKey)) {
            setHeaderKeyError(`Duplicate key name found: ${trimmedNewKey}`);
            // Don't update the actual key in the object, but keep the display value
            validateHeaderKeys(current);
            return;
        }

        // Clear error and update if valid
        setHeaderKeyError('');
        if (trimmedNewKey !== oldKey) {
            const {
                [oldKey]: value, ...rest
            } = current;
            const updated = {
                ...rest,
                [trimmedNewKey]: value
            };
            setFormData({
                ...formData,
                [type]: updated
            });
            // Update display values
            const newDisplayValues = {
                ...headerDisplayValues
            };
            delete newDisplayValues[oldKey];
            newDisplayValues[trimmedNewKey] = trimmedNewKey;
            setHeaderDisplayValues(newDisplayValues);
            // Validate all header keys after update
            validateHeaderKeys(updated);
        } else {
            // Key hasn't changed, just validate current headers
            validateHeaderKeys(current);
        }
    };

    const removeHeader = (type, key) => {
        const updated = {
            ...formData[type]
        };
        delete updated[key];
        setFormData({
            ...formData,
            [type]: updated
        });
    };

    const removeQueryParams = (type, index) => {
        const updatedHeaders = [...formData[type]];
        updatedHeaders.splice(index, 1);
        setFormData({
            ...formData,
            [type]: updatedHeaders
        });

        // Validate param keys after removal
        if (type === 'paramsDef') {
            validateParamKeys(updatedHeaders);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (btnLoading) return; // Prevent multiple submissions

        // Validate query param keys for duplicates
        if (formData.paramsDef && formData.paramsDef.length > 0) {
            if (!validateParamKeys(formData.paramsDef)) {
                setActiveTab('params');
                return; // Prevent form submission if there are duplicate keys
            }
        }

        // Validate header keys for duplicates
        if (formData.headers && Object.keys(formData.headers).length > 0) {
            if (!validateHeaderKeys(formData.headers)) {
                setActiveTab('headers');
                return; // Prevent form submission if there are duplicate keys
            }
        }

        // Validate authorization credentials (secrets are write-only / Vault-backed)
        if (formData.auth?.type === 'bearer') {
            if (!isEdit && !newBearerToken.trim()) {
                setAuthError('Please enter a bearer token or unselect the authorization type');
                setActiveTab('auth');
                return;
            }
            if (isEdit && !newBearerToken.trim() && !hasConfiguredSecret && !formData.auth?.token_ref) {
                setAuthError('Please enter a bearer token or unselect the authorization type');
                setActiveTab('auth');
                return;
            }
        } else if (formData.auth?.type === 'basic') {
            if (!formData.auth.username || !formData.auth.username.trim()) {
                setAuthError('Please enter a username or unselect the authorization type');
                setActiveTab('auth');
                return;
            }
            // Require a password whenever there is no Vault password_ref yet
            // (create, or switched to basic from another auth type).
            if (!newBasicPassword && !formData.auth?.password_ref) {
                setAuthError('Please enter a password or unselect the authorization type');
                setActiveTab('auth');
                return;
            }
        }

        const secrets = {};
        if (formData.auth?.type === 'bearer' && newBearerToken.trim()) {
            secrets.token = newBearerToken.trim();
        }
        if (formData.auth?.type === 'basic' && newBasicPassword.length > 0) {
            // Only send when the user typed a password (create or rotate).
            secrets.password = newBasicPassword;
        }

        // Sensitive headers: public map keeps names (empty values); new values go to Vault.
        const publicHeaders = {};
        const headerSecrets = {};
        const keepSecretHeaderNames = [];
        Object.entries(formData.headers || {}).forEach(([name, value]) => {
            const displayName = (headerDisplayValues[name] !== undefined
                ? headerDisplayValues[name]
                : name).trim() || name;
            const typed = newHeaderSecrets[name];
            const wasConfigured = (formData.secret_headers_configured || []).includes(name)
                || (formData.secret_headers_configured || []).includes(displayName)
                || isSensitiveHeaderName(displayName)
                || isSensitiveHeaderName(name);

            if (wasConfigured || isSensitiveHeader(displayName, String(value ?? ''))
                || (typeof typed === 'string' && typed.length > 0)) {
                keepSecretHeaderNames.push(displayName);
                if (typeof typed === 'string' && typed.length > 0) {
                    headerSecrets[displayName] = typed;
                } else if (typeof value === 'string' && value.length > 0
                    && isSensitiveHeader(displayName, value)) {
                    headerSecrets[displayName] = value;
                }
                // Keep an empty placeholder row name in headers for display only — stripped by service.
                publicHeaders[displayName] = '';
            } else {
                publicHeaders[displayName] = value;
            }
        });

        const updatedData = {
            ...formData,
            url: colonToBrace(formData.url),
            headers: publicHeaders,
            paramsDef: serializeParamsDef(formData.paramsDef),
            // Never embed plaintext secrets in the auth block — service writes Vault refs.
            auth: {
                type: formData.auth?.type || 'none',
                ...(formData.auth?.type === 'basic' ? { username: formData.auth.username } : {}),
                ...(formData.auth?.token_ref ? { token_ref: formData.auth.token_ref } : {}),
                ...(formData.auth?.password_ref ? { password_ref: formData.auth.password_ref } : {}),
                ...(formData.auth?.value_ref ? { value_ref: formData.auth.value_ref } : {}),
            },
            _vaultSecrets: secrets,
            _headerVaultSecrets: headerSecrets,
            _keepSecretHeaderNames: keepSecretHeaderNames,
        };

        // Input schema: parse if present, else send {} so clearing it actually persists.
        // An empty {} schema means "don't validate" (the engine validates only against a schema).
        if (formData.input_schema && formData.input_schema.trim()) {
            try {
                const value = JSON.parse(formData.input_schema);
                updatedData.input_schema = value;
            } catch (error) {
                setActiveTab('body');
                return; // Prevent form submission if JSON is invalid
            }
        } else {
            updatedData.input_schema = {};
        }

        // Response schema: parse if present, else send {} so clearing it actually persists.
        // Different APIs return different shapes — an empty schema = no output validation.
        if (formData.output_schema && formData.output_schema.trim()) {
            try {
                const value = JSON.parse(formData.output_schema);
                updatedData.output_schema = value;
            } catch (error) {
                setActiveTab('responseSchema');
                return; // Prevent form submission if schema JSON is invalid
            }
        } else {
            updatedData.output_schema = {};
        }

        setBtnLoading(true);
        await onSave(updatedData);
        setBtnLoading(false);
    };

    const handleDialogChange = (isOpen) => {
        if (!isOpen) {
            // When dialog is closed (via X button or outside click), call onCancel
            onCancel();
        }
        setOpen(isOpen);
    };

    // Vault-backed auth/headers only exist after Save. Block Send when:
    //  - never saved yet, or
    //  - user typed a new secret to rotate (must Save to Vault first), or
    //  - current auth type has no Vault-backed secret configured yet.
    // Do NOT use pristine here — path-param sync / hydrate can dirty the form
    // without changing credentials; Send may still use saved *_ref values.
    const hasPendingSecrets = Boolean(
        newBearerToken.trim()
        || newBasicPassword.length > 0
        || Object.values(newHeaderSecrets).some((v) => String(v || '').length > 0)
    );
    const authType = formData.auth?.type || 'none';
    const authSecretReady = authType === 'none'
        || (authType === 'bearer' && Boolean(formData.auth?.token_ref || formData.auth?.secrets_configured))
        || (authType === 'basic' && Boolean(formData.auth?.password_ref))
        || (authType === 'apikey' && Boolean(formData.auth?.value_ref || formData.auth?.secrets_configured));
    const mustSaveBeforeSend = !formData._id || hasPendingSecrets || !authSecretReady;

    function triggerSend() {
        if (mustSaveBeforeSend) {
            setShowTest(true);
            return;
        }
        setShowTest(true);
        setSendSignal((n) => n + 1);
    }

    const primaryActionClass = 'bg-violet-600 hover:bg-violet-700 text-white shadow-sm';

    return (
        <Dialog open={open} onOpenChange={handleDialogChange}>
            <DialogContent className={cn(
                'flex flex-col gap-0 overflow-hidden p-0',
                showTest
                    ? 'max-w-[1400px] w-[95vw] h-[88vh] max-h-[88vh]'
                    : 'w-full max-h-[88vh] max-w-3xl'
            )}>
                <DialogHeader className="flex-shrink-0 border-b px-6 pb-3 pt-5">
                    <div className="flex items-start justify-between gap-3">
                        <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
                            <span>{formData._id ? 'Edit API' : 'Create API'}</span>
                            {showTest && !pristine && (
                                <span className="rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-900">
                                    Unsaved
                                </span>
                            )}
                        </DialogTitle>
                        <div className="flex items-center gap-1">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setShowJson(true)}
                                title="View JSON"
                                className="h-8"
                            >
                                <Code className="mr-1 h-3 w-3" />
                                JSON
                            </Button>
                        </div>
                        {(
                            <div className="flex items-center gap-1">
                                <Button
                                    type="button"
                                    variant={showTest ? 'secondary' : 'outline'}
                                    size="sm"
                                    onClick={() => setShowTest((v) => !v)}
                                    title={showTest ? 'Hide test panel' : 'Open test panel'}
                                    className="h-8"
                                >
                                    <Play className="mr-1 h-3 w-3" />
                                    {showTest ? 'Hide test' : 'Test'}
                                </Button>
                                {showTest && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setTestMaximized((v) => !v)}
                                        title={testMaximized ? 'Restore split view' : 'Maximize test panel'}
                                        className="h-8 px-2"
                                    >
                                        {testMaximized
                                            ? <Minimize2 className="h-4 w-4" />
                                            : <Maximize2 className="h-4 w-4" />}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </DialogHeader>
                <div
                    className={cn(
                        'flex min-h-0 flex-1 overflow-hidden',
                        showTest && 'flex-row'
                    )}
                >
                    <form
                        onSubmit={handleSubmit}
                        className={cn(
                            'flex min-h-0 flex-col overflow-hidden bg-card',
                            showTest
                                ? cn(testMaximized ? 'hidden' : 'min-w-0 max-w-[50%] flex-1 border-r')
                                : 'w-full flex-1'
                        )}
                    >
                        <div className="flex-shrink-0 space-y-2 border-b px-6 pb-3 pt-4">
                            <div className="space-y-1">
                                <Label htmlFor="api-name-bar" className="text-[11px] text-muted-foreground">
                                    API name
                                </Label>
                                <Input
                                    id="api-name-bar"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="e.g. Geocode address"
                                    className="h-9 text-[15px] font-semibold"
                                    required
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <Select value={formData.method} onValueChange={handleMethodChange}>
                                    <SelectTrigger className="w-28 h-9 flex-shrink-0 font-semibold">
                                        <SelectValue placeholder="Method" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD', 'OPTIONS'].map((m) => (
                                            <SelectItem key={m} value={m}>{m}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Input
                                    name="url"
                                    value={formData.url}
                                    onChange={handleChange}
                                    onBlur={() => setFormData((p) => {
                                        const next = colonToBrace(p.url);
                                        return next === p.url ? p : { ...p, url: next };
                                    })}
                                    placeholder="https://api.example.com/endpoint"
                                    className="h-9 flex-1 font-mono text-[13px]"
                                    required
                                />
                                <Button
                                    type="button"
                                    onClick={triggerSend}
                                    disabled={mustSaveBeforeSend}
                                    className={cn('h-9 flex-shrink-0 gap-1', primaryActionClass)}
                                    title={mustSaveBeforeSend
                                        ? 'Save first — credentials are stored in Vault on Save, then Send can resolve them'
                                        : 'Send request — response appears in the test panel'}
                                >
                                    <Play className="h-3.5 w-3.5" /> Send
                                </Button>
                            </div>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
                        <Tabs className="w-full" value={activeTab} onValueChange={setActiveTab}>
                            <TabsList className="mb-4 grid w-full grid-cols-5">
                                <TabsTrigger value="params">Query params</TabsTrigger>
                                <TabsTrigger value="headers">Headers</TabsTrigger>
                                <TabsTrigger value="auth">Authorization</TabsTrigger>
                                <TabsTrigger disabled={formData.method === 'GET'} value="body">Body</TabsTrigger>
                                <TabsTrigger value="responseSchema">Response</TabsTrigger>
                            </TabsList>

                            <TabsContent value="params" className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <h3 className="text-sm font-semibold">Params</h3>
                                    <Button type="button" variant="outline" size="sm" onClick={addParams}>
                                        <Plus className="h-4 w-4 mr-2" />
                                        Add Params
                                    </Button>
                                </div>

                                {formData.paramsDef?.length === 0 ? (
                                    <div className="text-center p-4 border border-dashed rounded-md">
                                        <p className="text-sm text-muted-foreground">No params added yet</p>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {formData.paramsDef?.map((param, index) => (
                                            <div key={index} className="flex gap-2 items-center">
                                                <Input
                                                    value={param.key}
                                                    onChange={(e) => updateQueryParams('paramsDef', index, 'key', e.target.value)}
                                                    placeholder="Param name"
                                                    className="flex-1"
                                                />
                                                <Input
                                                    value={param.value}
                                                    onChange={(e) => updateQueryParams('paramsDef', index, 'value', e.target.value)}
                                                    placeholder="Value"
                                                    className="flex-1"
                                                />
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => removeQueryParams('paramsDef', index)}
                                                    className="h-9 w-9 text-destructive hover:text-destructive"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                    <span className="sr-only">Remove</span>
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {paramKeyError && (
                                    <Alert variant="destructive" className="mt-2">
                                        <AlertCircle className="h-4 w-4" />
                                        <AlertDescription>{paramKeyError}</AlertDescription>
                                    </Alert>
                                )}
                            </TabsContent>

                            <TabsContent value="headers" className="space-y-4">
                                <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-3 space-y-2 max-w-xs">
                                    <Label htmlFor="timeout" className="text-[11px] text-muted-foreground">
                                        Request timeout (ms)
                                    </Label>
                                    <Input
                                        type="number"
                                        min={500}
                                        id="timeout"
                                        name="timeout"
                                        value={formData.timeout}
                                        onChange={handleChange}
                                        className="h-9 bg-white"
                                        required
                                    />
                                </div>
                                <div className="flex justify-between items-center">
                                    <h3 className="text-sm font-semibold">Request Headers</h3>
                                    <Button type="button" variant="outline" size="sm" onClick={addHeader}>
                                        <Plus className="h-4 w-4 mr-2" />
                                        Add Header
                                    </Button>
                                </div>

                                {Object.keys(formData.headers)?.length === 0 ? (
                                    <div className="text-center p-4 border border-dashed rounded-md">
                                        <p className="text-sm text-muted-foreground">No headers added yet</p>
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {Object.entries(formData.headers).map(([key, value], index) => {
                                            const displayName = headerDisplayValues[key] !== undefined
                                                ? headerDisplayValues[key]
                                                : key;
                                            const secretHeader = isSensitiveHeaderName(displayName)
                                                || isSensitiveHeaderName(key)
                                                || (formData.secret_headers_configured || []).includes(key)
                                                || (formData.secret_headers_configured || []).includes(displayName)
                                                || isSensitiveHeader(displayName, String(value ?? ''));
                                            const configured = secretHeader && (
                                                (formData.secret_headers_configured || []).includes(key)
                                                || (formData.secret_headers_configured || []).includes(displayName)
                                                || isEdit
                                            );
                                            return (
                                                <div key={index} className="flex gap-2 items-center">
                                                    <Input
                                                        value={displayName}
                                                        onChange={(e) => updateHeaderKey('headers', key, e.target.value)}
                                                        placeholder="Header name"
                                                        className="flex-1"
                                                    />
                                                    <Input
                                                        type={secretHeader ? 'password' : 'text'}
                                                        value={secretHeader
                                                            ? (newHeaderSecrets[key] || '')
                                                            : value}
                                                        onChange={(e) => {
                                                            if (secretHeader) {
                                                                setNewHeaderSecrets((prev) => ({
                                                                    ...prev,
                                                                    [key]: e.target.value,
                                                                }));
                                                            } else {
                                                                updateHeader('headers', key, e.target.value);
                                                            }
                                                        }}
                                                        placeholder={secretHeader && configured
                                                            ? '<configured — enter new to rotate>'
                                                            : 'Value'}
                                                        className="flex-1"
                                                        autoComplete="off"
                                                    />
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => {
                                                            removeHeader('headers', key);
                                                            const newDisplayValues = {
                                                                ...headerDisplayValues
                                                            };
                                                            delete newDisplayValues[key];
                                                            setHeaderDisplayValues(newDisplayValues);
                                                            setNewHeaderSecrets((prev) => {
                                                                const next = { ...prev };
                                                                delete next[key];
                                                                return next;
                                                            });
                                                            setFormData((prev) => ({
                                                                ...prev,
                                                                secret_headers_configured: (
                                                                    prev.secret_headers_configured || []
                                                                ).filter((n) => n !== key && n !== displayName),
                                                            }));
                                                        }}
                                                        className="h-9 w-9 text-destructive hover:text-destructive"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                        <span className="sr-only">Remove</span>
                                                    </Button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {headerKeyError && (
                                    <Alert variant="destructive" className="mt-2">
                                        <AlertCircle className="h-4 w-4" />
                                        <AlertDescription>{headerKeyError}</AlertDescription>
                                    </Alert>
                                )}
                            </TabsContent>

                            <TabsContent value="auth" className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="auth-type">Authorization Type</Label>
                                    <Select value={formData.auth?.type} onValueChange={(value) => handleAuthTypeChange(value)}>
                                        <SelectTrigger id="auth-type">
                                            <SelectValue placeholder="Select authorization type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">None</SelectItem>
                                            <SelectItem value="bearer">Bearer Token</SelectItem>
                                            <SelectItem value="basic">Basic Auth</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                {formData.auth?.type === 'bearer' && (
                                    <div className="space-y-2">
                                        <Label htmlFor="token">
                                            Bearer Token
                                            {isEdit && hasConfiguredSecret ? ' (leave empty to keep existing)' : ''}
                                        </Label>
                                        <Input
                                            id="token"
                                            name="token"
                                            type="password"
                                            value={newBearerToken}
                                            onChange={(e) => { setNewBearerToken(e.target.value); setAuthError(''); }}
                                            placeholder={isEdit && hasConfiguredSecret
                                                ? '<configured — enter new to rotate>'
                                                : 'Enter your token'}
                                            autoComplete="off"
                                        />
                                    </div>
                                )}

                                {formData.auth?.type === 'basic' && (
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="username">Username</Label>
                                            <Input
                                                id="username"
                                                name="username"
                                                value={formData.auth.username || ''}
                                                onChange={handleAuthValueChange}
                                                placeholder="Username"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="password">
                                                Password
                                                {isEdit && hasConfiguredSecret ? ' (leave empty to keep existing)' : ''}
                                            </Label>
                                            <Input
                                                id="password"
                                                name="password"
                                                type="password"
                                                value={newBasicPassword}
                                                onChange={(e) => { setNewBasicPassword(e.target.value); setAuthError(''); }}
                                                placeholder={isEdit && hasConfiguredSecret
                                                    ? '<configured — enter new to rotate>'
                                                    : 'Password'}
                                                autoComplete="new-password"
                                            />
                                        </div>
                                    </div>
                                )}

                                {authError && (
                                    <Alert variant="destructive" className="mt-2">
                                        <AlertCircle className="h-4 w-4" />
                                        <AlertDescription>{authError}</AlertDescription>
                                    </Alert>
                                )}
                            </TabsContent>

                            <TabsContent value="body" className="space-y-4">
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center">
                                        <Label htmlFor="input_schema">Request Body</Label>
                                        <div className="flex items-center gap-2">
                                            {formData.method === 'GET' && (
                                                <span className="text-xs text-muted-foreground">
                                                    Note: Body is typically not used with GET requests
                                                </span>
                                            )}
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setInputSchemaBuilderOpen(true)}
                                            >
                                                <Wrench className="h-4 w-4 mr-2" />
                                                Schema Builder
                                            </Button>
                                            <SchemaSelector
                                                onSelect={(schema) => {
                                                    setFormData({
                                                        ...formData,
                                                        input_schema: schema
                                                    });
                                                    setJsonError('');
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <Textarea
                                        id="input_schema"
                                        name="input_schema"
                                        value={formData.input_schema}
                                        onChange={handleChange}
                                        placeholder={'{\n  "type": "object",\n  "properties": {\n    "id": { "type": "number" },\n    "name": { "type": "string" },\n    "active": { "type": "boolean" }\n  },\n  "required": ["id", "name"]\n}'}
                                        className="font-mono h-64"
                                    />

                                    {jsonError && (
                                        <Alert variant="destructive" className="mt-2">
                                            <AlertCircle className="h-4 w-4" />
                                            <AlertDescription>Invalid JSON: {jsonError}</AlertDescription>
                                        </Alert>
                                    )}
                                    <div className="text-xs text-muted-foreground">
                                        <p>Define the structure of the API request using JSON Schema format.</p>
                                        <p className="mt-1">Example: A user object with required id and name properties.</p>
                                    </div>
                                </div>
                            </TabsContent>

                            <TabsContent value="responseSchema" className="space-y-4">
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center">
                                        <Label htmlFor="output_schema">Expected Response Schema</Label>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setOutputSchemaBuilderOpen(true)}
                                            >
                                                <Wrench className="h-4 w-4 mr-2" />
                                                Schema Builder
                                            </Button>
                                            <SchemaSelector
                                                onSelect={(schema) => {
                                                    setFormData({
                                                        ...formData,
                                                        output_schema: schema
                                                    });
                                                    setSchemaError('');
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <Textarea
                                        id="output_schema"
                                        name="output_schema"
                                        value={formData.output_schema || ''}
                                        onChange={handleChange}
                                        placeholder={'{\n  "type": "object",\n  "properties": {\n    "id": { "type": "number" },\n    "name": { "type": "string" },\n    "active": { "type": "boolean" }\n  },\n  "required": ["id", "name"]\n}'}
                                        className="font-mono h-64"
                                    />

                                    {schemaError && (
                                        <Alert variant="destructive" className="mt-2">
                                            <AlertCircle className="h-4 w-4" />
                                            <AlertDescription>Invalid JSON: {schemaError}</AlertDescription>
                                        </Alert>
                                    )}

                                    <div className="text-xs text-muted-foreground">
                                        <p>Define the expected structure of the API response using JSON Schema format.</p>
                                        <p className="mt-1">Example: A user object with required id and name properties.</p>
                                    </div>
                                </div>
                            </TabsContent>
                        </Tabs>
                        </div>

                        <div className="flex flex-shrink-0 items-center justify-end gap-2 border-t bg-card px-6 py-3">
                            <Button type="button" variant="outline" onClick={onCancel}>
                                Cancel
                            </Button>
                            <Button
                                disabled={btnLoading}
                                type="submit"
                                className={primaryActionClass}
                            >
                                Save
                            </Button>
                        </div>
                    </form>
                    {showTest ? (
                        <div
                            className={cn(
                                'flex min-h-0 flex-1 flex-col overflow-hidden bg-[#f4f6fb]',
                                !testMaximized && 'min-w-0'
                            )}
                        >
                            {mustSaveBeforeSend ? (
                                <div className="flex-shrink-0 border-b border-amber-300/50 bg-amber-50/80 px-3 py-1.5 text-[11px] text-amber-900">
                                    <AlertCircle className="mr-1 inline h-3 w-3" />
                                    {!formData._id
                                        ? 'Save to store credentials in Vault, then Send.'
                                        : hasPendingSecrets
                                            ? 'Save to store the new secret in Vault, then Send.'
                                            : 'Save first — this auth type has no Vault credential configured yet.'}
                                </div>
                            ) : null}
                            <div className="min-h-0 flex-1">
                                <ApiTestPanel
                                    api={formData}
                                    maximized={testMaximized}
                                    runSignal={sendSignal}
                                />
                            </div>
                        </div>
                    ) : null}
                </div>
            </DialogContent>

            {/* Input Schema Builder Dialog */}
            <JSONSchemaBuilderDialog
                open={inputSchemaBuilderOpen}
                onOpenChange={setInputSchemaBuilderOpen}
                initialSchema={formData.input_schema}
                onSave={(schema) => {
                    setFormData({
                        ...formData,
                        input_schema: schema
                    });
                    setJsonError('');
                }}
                title="Request Body Schema Builder"
            />

            {/* Output Schema Builder Dialog */}
            <JSONSchemaBuilderDialog
                open={outputSchemaBuilderOpen}
                onOpenChange={setOutputSchemaBuilderOpen}
                initialSchema={formData.output_schema}
                onSave={(schema) => {
                    setFormData({
                        ...formData,
                        output_schema: schema
                    });
                    setSchemaError('');
                }}
                title="Response Schema Builder"
            />
            <EntityJsonView
                open={showJson}
                onOpenChange={setShowJson}
                title="API JSON"
                entity={formData}
                onApply={(parsed) => {
                    if (!parsed || typeof parsed !== 'object') return;
                    setFormData(parsed);
                }}
            />
        </Dialog>
    );
}
